<template>
    <div class="font-sans relative">
        <div v-if="$slots.before">
            <slot name="before" :focusTextarea="focusTextarea"></slot>
        </div>

        <div class="
        relative
        scroll-thin
        font-sans
        bg-white
        transition
        border
        border-solid
        rounded-xl
        focus-within:outline-none
        focus-within:ring-2
        focus-within:ring-blue-500
        focus-within:ring-offset-2
        focus-within:ring-offset-slate-50
    "
            :class="{
                'border-slate-300': !isDisabled && !isInvalid && !props.floating,
                'text-slate-500 cursor-not-allowed pointer-events-none': isDisabled,
                'border-slate-200': isDisabled && !props.floating,
                'text-rose-600 focus-within:ring-rose-500!': isInvalid,
                'shadow border-white': props.floating,
            }">

            <div
                v-if="snippetLoading"
                data-test="snippet-loading"
                class="absolute inset-0 w-full h-full bg-white/80 flex items-center justify-center z-10 rounded-xl"
            >
                <div class="text-slate-500">
                    <vx-icon-loading class="animate-loading-spin text-2xl"></vx-icon-loading>
                </div>
            </div>

            <vx-alert v-if="lockedUiActive" color="info" :closeable="false" shadow class="rounded-t-xl rounded-b-none relative">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faLock" widthAuto />
                </template>

                <span v-if="!selectedSnippet.id">
                    You can only choose from <strong>Corporate Locked Snippets</strong> at this time.
                </span>
                <span v-else><strong>Corporate Locked Snippets</strong> cannot be edited.</span>
                <span v-if="selectedSnippet?.fields?.length > 0">
                    Use fields below to customize.
                </span>

                <template v-slot:right>
                    <vx-button v-if="props.onlyLockedSnippetsAllowed && selectedSnippet.id" @click="snippetsModal = true" size="sm" class="mix-blend-multiply" color="muted">
                        Change snippet
                        <font-awesome-icon widthAuto :icon="faRotate"/>
                    </vx-button>
                    <vx-button v-else-if="selectedSnippet.id" @click="clearMessage()" size="sm" class="mix-blend-multiply" color="muted">
                        Remove
                        <font-awesome-icon widthAuto :icon="faXmark"/>
                    </vx-button>
                </template>
            </vx-alert>

            <textarea :value="props.modelValue"
                :maxlength="props.maxlength"
                @input="emit('update:modelValue', $event.target.value)"
                @blur="cursorLocation = $event.target.selectionEnd; emit('blur', $event);"
                @paste="pasteImage"
                @keydown.meta.enter="submit"
                @keydown.ctrl.enter="submit"
                ref="textarea"
                :placeholder="props.placeholder || (lockedUiActive ? 'Choose a snippet' : '')"
                :disabled="isDisabled || lockedUiActive"
                :aria-invalid="props.error ? true : undefined"
                class="
                    px-4
                    pt-4
                    pb-0
                    bg-white
                    font-sans
                    text-inherit
                    text-base
                    min-h-[38px]
                    rounded-xl
                    resize-none
                    border-0
                    w-full
                    focus:outline-none
                "
                :class="{
                    'text-slate-800': !isDisabled && !lockedUiActive,
                    'text-slate-400': isDisabled || lockedUiActive,
                    'max-h-[50vh] overflow-auto': props.maxHeight,
                    'overflow-hidden': !props.maxHeight,
                }" />
            <div v-if="uploadingPastedImage"
                class="mx-4 mt-2 flex items-center gap-2 text-sm text-slate-500"
                data-test="pasted-image-uploading">
                <vx-icon-loading class="animate-loading-spin"></vx-icon-loading>
                Uploading image...
            </div>
            <div v-if="props.mediaUrl" class="w-32 mx-4 mt-2">
                <message-builder-media-preview :mediaUrl="props.mediaUrl"
                    :mediaContentType="props.mediaContentType"
                    @remove="removeMedia()"
                    :removeable="!selectedSnippet.is_locked"
                ></message-builder-media-preview>
            </div>
            <div class="pb-2 px-2 grid grid-cols-2 md:flex md:justify-between md:items-center">
                <vx-button
                    v-if="lockedUiActive"
                    @click="snippetsModal = true"
                    color="transparent"
                    type="button">
                    <font-awesome-icon widthAuto class="text-sm" :icon="faCirclePlus"></font-awesome-icon>
                    Snippets
                </vx-button>

                <message-builder-actions
                    v-else
                    ref="actionsRef"
                    class="order-2"
                    @click:ai="emit('click:ai')"
                    @click:variables="openVariableModal()"
                    @click:snippets="snippetsModal = true"
                    @click:emojis="openEmojiPicker()"
                    @click:media="openMediaModal()"
                    :disabled="isDisabled"
                    :enableAi="props.enableAi"
                    :disableSnippets="props.disableSnippets"
                    :disableMedia="props.disableMedia"
                    :disableEmojis="props.disableEmojis || hasLockedSnippet"
                    :blockImages="props.blockImages"
                    :blockFiles="props.blockFiles"
                >
                    <slot name="actions"></slot>
                    <template v-slot:append>
                        <slot name="actionsAppend"></slot>
                    </template>
                </message-builder-actions>

                <div v-if="!hideNumbers"
                    class="text-xs text-slate-500 col-span-2 order-1 text-center mb-2 md:mb-0 md:order-2">
                    <message-builder-numbers v-if="!props.disabled" :body="props.modelValue"
                        :mediaUrl="props.mediaUrl"></message-builder-numbers>
                </div>
                <div class="flex items-center order-3 justify-self-end">
                    <vx-dropdown v-if="!props.preview && !props.testable" teleport>
                        <vx-button type="button" :disabled="isDisabled"
                            :size="!isMd ? 'sm' : undefined"
                            color="muted-light">
                            <font-awesome-icon widthAuto :icon="faEllipsis"
                                class="text-sm"></font-awesome-icon>
                        </vx-button>

                        <template v-slot:items>
                            <vx-dropdown-item v-if="!props.disableSchedule && (onCorporate() || currentGroup?.group_id)" @click="scheduleModal = true">
                                <span>
                                    <font-awesome-icon widthAuto :icon="faCalendar"
                                        class="mr-1 text-sm"></font-awesome-icon>
                                    Schedule
                                </span>
                            </vx-dropdown-item>
                            <vx-dropdown-item :disabled="!props.modelValue" @click="previewModal = true">
                                <span>
                                    <font-awesome-icon widthAuto :icon="faEye" class="mr-1 text-sm"></font-awesome-icon>
                                    Preview
                                </span>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>

                    <vx-button v-if="!props.preview && !props.testable"
                        type="button"
                        :size="!isMd ? 'sm' : undefined"
                        :disabled="isDisabled"
                        data-pendo="message-builder-send-button"
                        data-test="send-button"
                        class="ml-2"
                        @click.prevent="submit">
                        Send
                        <font-awesome-icon widthAuto :icon="faPaperPlane"
                            class="text-sm"></font-awesome-icon>
                    </vx-button>

                    <vx-button v-if="props.preview"
                        type="button"
                        size="sm"
                        :disabled="isDisabled || !props.modelValue"
                        class="ml-2"
                        @click.prevent="previewModal = true"
                        color="muted">
                        Preview
                        <font-awesome-icon widthAuto :icon="faEye" class="text-sm"></font-awesome-icon>
                    </vx-button>

                    <vx-button v-if="props.testable"
                        type="button"
                        size="sm"
                        :disabled="isDisabled || !props.modelValue"
                        class="ml-2"
                        @click.prevent="sendTest()"
                        color="muted">
                        Send Test
                        <font-awesome-icon widthAuto :icon="faFlaskVial"
                            class="text-sm"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
            <div v-if="$slots.alert"
                class="absolute inset-s-0 inset-bs-0 w-full h-full flex items-center justify-center">
                <div>
                    <slot name="alert"></slot>
                </div>
            </div>
        </div>
        <div v-if="props.error || $slots.error || snippetError"
            class="text-xs text-rose-800 mt-2">
            <slot name="error"></slot>
            {{ props.error || snippetError }}
        </div>

        <vx-alert v-if="droppedSnippetMedia" class="mt-2" color="info" :closeable="false">
            The snippet attachment has been dropped because attachments are not supported here.
        </vx-alert>

        <div
            v-if="hasLockedSnippet && selectedSnippet?.fields?.length > 0"
            class="mt-6">
            <vx-label size="md" class="mb-2">
                Fields
            </vx-label>
            <div class="flex flex-col gap-4 bg-slate-300/30 rounded-2xl p-6 shadow-inner border-b border-b-white">
                <message-builder-snippet-field
                    v-for="(field, index) in selectedSnippet.fields"
                    :modelValue="snippetFieldBindings.find(b => b.key === field.key)?.value ?? null"
                    @update:modelValue="updateFieldBinding(field.key, $event)"
                    :index="index"
                    :key="field.id"
                    :field="field"
                ></message-builder-snippet-field>
            </div>
        </div>

        <message-preview-modal v-model:visible="previewModal"
            :message="{
                body: previewBodyMessage,
                mediaUrl: props.mediaUrl,
                mediaContentType: props.mediaContentType,
            }">
            <template v-slot:header>
                <slot name="previewHeader"></slot>
            </template>
            <template v-slot:subheader>
                <slot name="previewSubheader"></slot>
            </template>
            <template v-slot:body>
                <slot name="previewBody"></slot>
            </template>
            <template v-if="!props.preview"
                v-slot:footer>
                <vx-button type="button" @click.prevent="previewModal = false"
                    size="lg"
                    color="muted">
                    Cancel
                    <font-awesome-icon widthAuto :icon="faCircleX"></font-awesome-icon>
                </vx-button>
                <vx-button type="button" @click.prevent="submit"
                    size="lg"
                    color="primary"
                    class="lg:grow">
                    Send
                    <font-awesome-icon widthAuto :icon="faPaperPlane"></font-awesome-icon>
                </vx-button>
            </template>
            <template v-else
                v-slot:footer>
                <vx-button type="button" @click.prevent="previewModal = false"
                    size="lg"
                    block
                    color="primary">
                    Ok
                </vx-button>
            </template>
        </message-preview-modal>
        <message-builder-snippets v-if="!props.disableSnippets"
            v-model:visible="snippetsModal"
            :disableMedia="props.disableMedia"
            :blockFiles="props.blockFiles"
            :blockImages="props.blockImages"
            :onlyLockedSnippetsAllowed="props.onlyLockedSnippetsAllowed || props.blastForecastMode"
            :disableLockedSnippets="props.disableLockedSnippets"
            :contextOrgUnitId="effectiveOrgUnitId"
            :contextGroupId="effectiveGroupId"
            @selected="snippetSelected($event)">
            <template v-if="slots['blocked-content']" v-slot:blocked-content>
                <slot name="blocked-content"></slot>
            </template>
        </message-builder-snippets>
        <message-builder-swap-snippet-media v-if="!props.disableSnippets && !props.disableMedia"
            :snippet="swapSnippetMediaModal"
            :currentMediaUrl="props.mediaUrl"
            :currentMediaContentType="props.mediaContentType"
            @close="swapSnippetMediaModal = undefined"
            @no="snippetSelected($event)"
            @yes="snippetSelected($event, true)"></message-builder-swap-snippet-media>
        <message-builder-schedule v-model:visible="scheduleModal"
            @schedule="schedule" :sendAt="props.sendAt"></message-builder-schedule>
        <message-builder-variables v-model:visible="variablesModal"
            :externalContactLists="props.externalContactLists"
            @selected="insertContent($event)"></message-builder-variables>
        <message-builder-emojis class="absolute inset-be-14 inset-s-14"
            v-model:visible="emojisPicker"
            @selected="insertContent($event.native);"></message-builder-emojis>
        <message-builder-media v-model:visible="mediaModal"
            ref="mediaModalRef"
            @update:visible="!$event && textarea.focus()"
            :mediaUrl="props.mediaUrl"
            @update:mediaUrl="emit('update:mediaUrl', $event)"
            :mediaContentType="props.mediaContentType"
            @update:mediaContentType="emit('update:mediaContentType', $event)"
            :blockFiles="props.blockFiles"
            :blockImages="props.blockImages"
        >
        </message-builder-media>
        <message-builder-test
            v-model:visible="testModal"
            :orgUnitId="effectiveOrgUnitId"
            :groupId="effectiveGroupId"
            :bodyMessage="props.modelValue"
            :mediaUrl="props.mediaUrl"
            :mediaContentType="props.mediaContentType"
            :snippetId="snippetId"
            :snippetFieldBindings="snippetFieldBindings"
            :onlyLockedSnippetsAllowed="props.onlyLockedSnippetsAllowed"
            :disableLockedSnippets="props.disableLockedSnippets"
        ></message-builder-test>
    </div>
</template>

<script setup>
import { faCalendar, faEllipsis, faEye } from '@fortawesome/pro-regular-svg-icons';
import {
    faCirclePlus,
    faCircleX,
    faFlaskVial,
    faLock,
    faPaperPlane,
    faRotate,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxDropdown, VxDropdownItem, VxIconLoading, VxLabel } from '@voxie/frontend-components';
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue';
import MessageBuilderNumbers from './MessageBuilderNumbers.vue';
import MessageBuilderActions from './MessageBuilderActions.vue';
import MessagePreviewModal from './modals/MessagePreviewModal.vue';
import MessageBuilderSchedule from './modals/MessageBuilderSchedule.vue';
import MessageBuilderVariables from './modals/MessageBuilderVariables.vue';
import MessageBuilderSnippets from './modals/MessageBuilderSnippets.vue';
import MessageBuilderSwapSnippetMedia from './modals/MessageBuilderSwapSnippetMedia.vue';
import MessageBuilderEmojis from './modals/MessageBuilderEmojis.vue';
import MessageBuilderMedia from './modals/MessageBuilderMedia.vue';
import MessageBuilderMediaPreview from './modals/MessageBuilderMediaPreview.vue';
import MessageBuilderTest from './modals/MessageBuilderTest.vue';
import MessageBuilderSnippetField from '~/components/general/message-builder/MessageBuilderSnippetField.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { snippetsClient } from '~/services/index';
import { getSnippetPreviewMessage } from '~/components/quick-blast/snippets';
import useVuelidate from '@vuelidate/core';
import { useToasted } from '~/composables/useToasted';
import { ALLOWED_IMAGE_TYPES } from '~/utils/message';

const teamId = Spark.state.currentTeam.id;

const slots = useSlots();
const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();
const { isMd } = useResponsiveLayout();

const toasted = useToasted();

const props = defineProps({
    modelValue: {
        type: String,
        required: true,
    },
    mediaUrl: {
        type: [String, null],
    },
    placeholder: {
        type: String,
    },
    mediaContentType: {
        type: [String, null],
    },
    disabled: {
        type: Boolean,
    },
    error: {
        type: String,
    },
    floating: {
        type: Boolean,
    },
    preview: {
        type: Boolean,
        default: false,
    },
    testable: {
        type: Boolean,
        default: false,
    },
    onlyLockedSnippetsAllowed: {
        type: Boolean,
        default: false,
    },
    disableLockedSnippets: {
        type: Boolean,
        default: true,
    },
    enableAi: {
        type: Boolean,
        default: false,
    },
    disableSnippets: {
        type: Boolean,
        default: false,
    },
    disableMedia: {
        type: Boolean,
        default: false,
    },
    disableEmojis: {
        type: Boolean,
        default: false,
    },
    disableSchedule: {
        type: Boolean,
        default: false,
    },
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
    hideNumbers: {
        type: Boolean,
        default: false,
    },
    maxlength: {
        type: [String, Number],
    },
    externalContactLists: {
        type: Array,
        default: () => [],
    },
    sendAt: {
        type: String,
    },
    maxHeight: {
        type: Boolean,
        default: false,
    },
    initialSnippet: {
        type: Object,
        default: null,
    },
    contextOrgUnitId: {
        type: Number,
        default: undefined,
    },
    contextGroupId: {
        type: String,
        default: undefined,
    },
    blastForecastMode: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits([
    'blur',
    'update:modelValue',
    'update:mediaUrl',
    'update:mediaContentType',
    'update:snippet',
    'selected:snippet',
    'click:ai',
    'schedule',
    'submit',
]);

const effectiveOrgUnitId = computed(() => {
    if (props.contextGroupId !== undefined) {
        return undefined;
    }

    return props.contextOrgUnitId ?? currentOrgUnit?.value?.id;
});

const effectiveGroupId = computed(() => {
    if (props.contextOrgUnitId !== undefined) {
        return undefined;
    }

    return props.contextGroupId ?? currentGroup?.value?.group_id;
});

const validation = useVuelidate();

const actionsRef = ref();
const actionsDropdown = computed(() => {
    if (!actionsRef.value) {
        return false;
    }

    return actionsRef.value.open;
})
const textarea = ref();
const cursorLocation = ref(props.modelValue.length);

const submit = (ev) => {
    ev.preventDefault();
    emit('submit');
}

const focusTextarea = async ({ cursorAtEnd = false } = {}) => {
    await nextTick();

    if (!textarea.value) {
        return;
    }

    textarea.value.focus();

    if (!cursorAtEnd) {
        return;
    }

    const cursorPosition = textarea.value.value.length;
    textarea.value.selectionStart = cursorPosition;
    textarea.value.selectionEnd = cursorPosition;
    cursorLocation.value = cursorPosition;
}

const emojisPicker = ref(false);
const mediaModal = ref(false);
const scheduleModal = ref(false);
const snippetsModal = ref(false);
const variablesModal = ref(false);

const insertContent = async (content) => {
    if (!textarea.value) {
        emit('update:modelValue', props.modelValue + content);
        return;
    }

    const start = textarea.value.value.substring(0, cursorLocation.value);
    const end = textarea.value.value.substring(cursorLocation.value);

    emit('update:modelValue', start + content + end);

    await nextTick();

    textarea.value.selectionStart = cursorLocation.value + content.length;
    textarea.value.selectionEnd = cursorLocation.value + content.length;

    cursorLocation.value = textarea.value.selectionStart;

    textarea.value.focus();
}

const droppedSnippetMedia = ref(false);
const swapSnippetMediaModal = ref(undefined);
const snippetLoading = ref(false);
const snippetError = ref('');
const snippetId = defineModel('snippetId', {
    type: Number,
    default: null,
});
const snippetFieldBindings = defineModel('snippetFieldBindings', {
    type: Array,
    get: (v) => v ?? [],
});

const updateFieldBinding = (key, value) => {
    const index = snippetFieldBindings.value.findIndex(fieldBinding => fieldBinding.key === key);
    if (index >= 0) {
        snippetFieldBindings.value = snippetFieldBindings.value.map((fieldBinding, i) =>
            i === index ? { ...fieldBinding, value } : fieldBinding
        );
    } else {
        snippetFieldBindings.value = [...snippetFieldBindings.value, { key, value }];
    }
};

const selectedSnippet = ref({});

const previewBodyMessage = computed(() => {
    if (hasLockedSnippet.value && selectedSnippet.value.body && (snippetFieldBindings.value || []).length > 0) {
        return getSnippetPreviewMessage(selectedSnippet.value.body, snippetFieldBindings.value);
    }

    return props.modelValue;
});

const hasLockedSnippet = computed(() => !props.disableLockedSnippets && selectedSnippet.value.is_locked);

const lockedUiActive = computed(() =>
    (props.onlyLockedSnippetsAllowed && !props.blastForecastMode) || hasLockedSnippet.value
);

const snippetSelected = async (snippet, replace) => {
    if (
        replace == undefined &&
        !props.disableMedia &&
        snippet.media_url &&
        props.mediaUrl &&
        props.mediaUrl !== snippet.media_url &&
        !snippet.is_locked &&
        !selectedSnippet.value.is_locked
    ) {
        swapSnippetMediaModal.value = snippet;
        return;
    }

    const oldFieldBindings = snippetFieldBindings.value || [];

    if (
        (props.onlyLockedSnippetsAllowed && !snippet.is_locked) ||
        (props.disableLockedSnippets && snippet.is_locked)
    ) {
        return;
    }

    // avoid fetching twice, fields is missing when calling `getSnippets` for the list, but present when the component is mounted (onMounted)
    if (snippet.is_locked && snippet.fields === undefined) {
        const snippetResponse = await getSnippet(snippet.id);

        if (snippetResponse) {
            snippet = { ...snippetResponse };
        }
    }

    const newFieldBindings = [];
    snippet.fields?.forEach((field) => {
        const existingValue = oldFieldBindings.find(b => b.key === field.key)?.value ?? null;
        newFieldBindings.push({ key: field.key, value: existingValue });
    });
    snippetFieldBindings.value = newFieldBindings;

    selectedSnippet.value = { ...snippet };
    snippetId.value = snippet.id || null;

    if (!props.onlyLockedSnippetsAllowed && !snippet.is_locked) {
        snippetFieldBindings.value = [];
    }

    emit('selected:snippet', snippet);
    emit('update:modelValue', snippet.body);
    await nextTick();
    if (!props.disableMedia && snippet.media_url) {
        emit('update:mediaContentType', snippet.media_content_type);
        await nextTick();
        emit('update:mediaUrl', snippet.media_url);
    } else if (!props.disableMedia && snippet.is_locked && props.mediaUrl) {
        await removeMedia();
    }

    droppedSnippetMedia.value = props.disableMedia && Boolean(snippet.media_url);
    await nextTick();
    textarea.value.focus();
}

const getSnippet = async (snippetId) => {
    snippetLoading.value = true;
    snippetError.value = '';

    try {
        const response = await snippetsClient.getSnippet({ teamId }, snippetId);
        return response.data;
    } catch (error) {
        console.log(error);
        snippetError.value = 'Failed to load snippet details.';
    } finally {
        snippetLoading.value = false;
    }
};

const clearMessage = () => {
    selectedSnippet.value = {};
    snippetId.value = null;
    snippetFieldBindings.value = [];
    emit('update:modelValue', '');
    emit('selected:snippet', null);
    removeMedia();
};

const openVariableModal = () => {
    if (hasLockedSnippet.value) {
        toasted.error('Cannot add variables with a locked snippet')
        return;
    }

    variablesModal.value = true
}

const openMediaModal = () => {
    if (hasLockedSnippet.value) {
        toasted.error('Cannot add media with a locked snippet')
        return;
    }

    mediaModal.value = true
}

const mediaModalRef = ref();

const uploadingPastedImage = ref(false);

const pastedImageWithoutText = (clipboardData) => {
    if (!clipboardData || '' !== clipboardData.getData('text/plain')) {
        return undefined;
    }

    return [...clipboardData.files].find((file) => ALLOWED_IMAGE_TYPES.includes(file.type));
}

const attachPastedImage = async (image) => {
    uploadingPastedImage.value = true;

    try {
        const media = await mediaModalRef.value.uploadMedia(image);

        if (hasLockedSnippet.value) {
            toasted.error('Cannot add media with a locked snippet');
            return;
        }

        emit('update:mediaContentType', media.contentType);
        await nextTick();
        emit('update:mediaUrl', media.url);

        if (media.warning) {
            toasted.info(media.warning);
        }
    } catch (e) {
        toasted.error(e.message);
    } finally {
        uploadingPastedImage.value = false;
    }
}

const pasteImage = (event) => {
    const image = pastedImageWithoutText(event.clipboardData);

    if (!image || props.disableMedia || props.blockImages || uploadingPastedImage.value) {
        return;
    }

    event.preventDefault();
    attachPastedImage(image);
}

const openEmojiPicker = () =>{
    if (hasLockedSnippet.value) {
        toasted.error('Cannot add emojis with a locked snippet')
        return;
    }

    emojisPicker.value = true
}

const removeMedia = async () => {
    emit('update:mediaUrl');
    await nextTick();
    emit('update:mediaContentType');
}

const isDisabled = computed(() => {
    return props.disabled || slots.alert?.() !== undefined;
})

const isInvalid = computed(() => {
    return props.error || slots.error?.().length || props.modelValue.length >= 16000;
});

const testModal = ref(false);
const previewModal = ref(false);

const sendTest = async () => {
    if (props.onlyLockedSnippetsAllowed && selectedSnippet.value.is_locked) {
        // Call parent validation to trigger child component validation
        const isValid = await validation.value.$validate();

        if (!isValid) {
            return;
        }
    }

    testModal.value = true;
};

const schedule = (scheduleAt) => {
    emit('schedule', scheduleAt);
    focusTextarea();
}

const resize = () => {
    if (!textarea.value) {
        return;
    }

    // Reset height to default single line
    textarea.value.style.height = '38px';

    // Only adjust height if content requires more space
    if (textarea.value.scrollHeight > textarea.value.offsetHeight) {
        textarea.value.style.height = textarea.value.scrollHeight + 'px';
    }
}

watch(() => props.modelValue, () => {
    nextTick().then(resize)
}, {
    immediate: true,
});

const modalOpen = computed(() => {
    return previewModal.value || variablesModal.value || mediaModal.value || scheduleModal.value || snippetsModal.value || testModal.value || Boolean(swapSnippetMediaModal.value)
})

onMounted(async () => {
    if (props.initialSnippet) {
        snippetSelected(props.initialSnippet);
        return;
    }

    if (!snippetId.value) {
        return;
    }

    const snippet = await getSnippet(snippetId.value);
    if (snippet) {
        snippetSelected(snippet)
    }
});

defineExpose({
    scheduleModal: scheduleModal,
    emojisPicker: emojisPicker,
    actionsDropdown: actionsDropdown,
    modalOpen: modalOpen,
    textarea: textarea,
    focusTextarea: focusTextarea,
    snippetLoading: snippetLoading,
    insertContent: insertContent,
});
</script>
